import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { TextField } from '@/components/TextField/TextField';
import { isValidSlug, slugFromName } from '@/lib/tenants';

import styles from './OrganizationForm.module.css';

export interface OrganizationDraft {
  name: string;
  slug: string | null;
  extAppApiId: string | null;
}

export interface OrganizationFormProps {
  busy?: boolean;
  error?: string | null;
  onSubmit: (draft: OrganizationDraft) => void;
  onCancel: () => void;
}

const SLUG_RULE = 'Use lowercase letters and numbers, with single hyphens between words.';

/** Create an umbrella organization; tenants are linked to it afterwards. */
export function OrganizationForm({
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: OrganizationFormProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [extAppApiId, setExtAppApiId] = useState('');
  const [touched, setTouched] = useState(false);

  const effectiveSlug = (slugEdited ? slug : slugFromName(name)).trim();
  const nameError = touched && !name.trim() ? 'Give the organization a name.' : undefined;
  const slugError =
    touched && effectiveSlug !== '' && !isValidSlug(effectiveSlug) ? SLUG_RULE : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!name.trim() || (effectiveSlug !== '' && !isValidSlug(effectiveSlug))) {
      return;
    }
    onSubmit({
      name: name.trim(),
      slug: effectiveSlug || null,
      extAppApiId: extAppApiId.trim() || null,
    });
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button form="organization-form" loading={busy} type="submit">
            Add organization
          </Button>
        </>
      }
      description="Link tenants to it once it exists."
      dismissable={!busy}
      onClose={onCancel}
      title="Add an organization"
    >
      <form id="organization-form" className={styles.form} noValidate onSubmit={submit}>
        <TextField
          autoComplete="off"
          data-autofocus
          error={nameError}
          label="Organization name"
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Acme Group"
          value={name}
        />
        <TextField
          autoComplete="off"
          error={slugError}
          hint="Filled in from the name. Leave empty for none."
          label="Slug"
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(event.target.value);
          }}
          value={slugEdited ? slug : effectiveSlug}
        />
        <TextField
          autoComplete="off"
          hint="Optional. The matching record in app-api, if there is one."
          label="app-api id"
          onChange={(event) => setExtAppApiId(event.target.value)}
          value={extAppApiId}
        />
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
